import { capitalize } from 'es-toolkit';
import React, { KeyboardEvent, useCallback, useRef } from 'react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';

import { useDelayedBoolean } from '@paragon/client-tools';

interface Props {
  readonly open: boolean;
  readonly defaultValue: string;

  readonly verb: 'create' | 'rename';
  readonly noun: 'plate list' | 'lumber list' | 'hanger list';

  onClose(): void;
  onSubmit(name: string): Promise<void>;
}

// Something weird is happening with MUI where if this dialog is launched from an IconButton, that button will be
//   focused (and this highlighted) when the dialog is closed. That's ugly, so this is the "solution."
function blurDocument() {
  if (document.activeElement instanceof HTMLElement) {
    document.activeElement.blur();
  }
}

export function NameDialog({ open, defaultValue, verb, noun, onClose, onSubmit }: Props) {
  const nameFieldRef = useRef<any>(undefined); // TODO: typescript

  const [isSubmitting, isSubmittingSlowly, setIsSubmitting] = useDelayedBoolean();

  const closeDialog = useCallback(() => {
    if (isSubmitting) return;
    onClose();
    blurDocument();
  }, [isSubmitting, onClose]);

  const onSubmitClick = useCallback(async () => {
    setIsSubmitting(true);

    try {
      await onSubmit(nameFieldRef.current.value);
    } finally {
      setIsSubmitting(false);
    }

    // this prevents a stale name from appearing if the dialog is re-opened
    nameFieldRef.current.value = null;

    onClose();
    blurDocument();
  }, [onClose, onSubmit, setIsSubmitting]);

  const onKeyUp = useCallback(
    async (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        event.stopPropagation();
        await onSubmitClick();
      }
    },
    [onSubmitClick],
  );

  return (
    <Dialog open={open} onClose={closeDialog}>
      <DialogTitle>{([verb] as string[]).concat(noun.split(' ')).map(capitalize).join(' ')}</DialogTitle>
      <DialogContent sx={{ minWidth: '400px' }}>
        <TextField
          sx={{ m: 'auto', marginTop: 1 }}
          autoFocus
          onFocus={(event) => event.target.select()}
          fullWidth
          label="Name"
          defaultValue={defaultValue}
          inputRef={nameFieldRef}
          onKeyUp={onKeyUp}
        />
      </DialogContent>
      <DialogActions>
        {isSubmittingSlowly ? (
          <CircularProgress />
        ) : (
          <>
            <Button variant="outlined" disabled={isSubmitting} onClick={closeDialog}>
              Cancel
            </Button>
            <Button variant="contained" disabled={isSubmitting} onClick={onSubmitClick}>
              {verb}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}
